import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  ShoppingCart,
  Search,
  Clock,
  Truck,
  CheckCircle,
  XCircle,
  RefreshCw,
  Plus,
  RotateCcw
} from 'lucide-react';
import { toast } from 'react-toastify';
import { getOrders, updateOrderStatus } from '../../api/wholesale/orders';
import CreateOrderModal from '../../components/wholesale/CreateOrderModal';
import { useFiscalPeriod } from '../../contexts/FiscalPeriodContext';
import PeriodFilter from '../../components/common/PeriodFilter';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const WholesaleOrdersPage = () => {
  const { periodRange } = useFiscalPeriod();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') || 'all';
  const paymentStatusParam = searchParams.get('payment_status') || 'all';
  const [paymentStatus, setPaymentStatus] = useState(paymentStatusParam);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [actionLoading, setActionLoading] = useState(null);
  const navigate = useNavigate();

  // Local period filter state (null = use global, otherwise override)
  const [localPeriod, setLocalPeriod] = useState(null);

  // Current active date range (local override or global)
  const activeDateRange = useMemo(() => {
    if (localPeriod) {
      return {
        start_date: localPeriod.start_date || null,
        end_date: localPeriod.end_date || null,
        label: localPeriod.label
      };
    }
    return {
      start_date: periodRange?.start_date || null,
      end_date: periodRange?.end_date || null,
      label: periodRange?.period_label || 'All Time'
    };
  }, [localPeriod, periodRange]);

  // Handle period filter change
  const handlePeriodChange = useCallback((range) => {
    if (range.isGlobal) {
      setLocalPeriod(null);
    } else {
      setLocalPeriod(range);
    }
  }, []);

  // Determine if user explicitly chose "All Time" (local override with no dates)
  const isExplicitAllTime = localPeriod && !localPeriod.start_date && !localPeriod.end_date;

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Build date params from active date range
      const dateParams = {};
      if (activeDateRange.start_date) dateParams.start_date = activeDateRange.start_date;
      if (activeDateRange.end_date) dateParams.end_date = activeDateRange.end_date;

      // Build API params - pass status filter if it's a specific status (not 'all' or 'in_progress')
      const apiParams = {
        ...dateParams,
        limit: 1000
      };

      // Tell backend to skip global fiscal period fallback when "All Time" is explicitly selected
      if (isExplicitAllTime && !apiParams.start_date && !apiParams.end_date) {
        apiParams.ignore_global_period = true;
      }

      if (statusFilter && statusFilter !== 'all' && statusFilter !== 'in_progress') {
        apiParams.status = statusFilter;
      }

      if (paymentStatus && paymentStatus !== 'all') {
        apiParams.payment_status = paymentStatus;
      }

      const response = await getOrders(apiParams);

      const data = response?.data || response || [];
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [activeDateRange.start_date, activeDateRange.end_date, statusFilter, paymentStatus]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const statusConfig = {
    // Status 1: New Order (WhatsApp only)
    new_order: { label: 'New Order', color: 'bg-sky-100 text-sky-700', icon: Clock },
    // Status 2: Parsed (WhatsApp only)
    parsed: { label: 'Parsed', color: 'bg-teal-100 text-teal-700', icon: CheckCircle },
    // Status 3: Pending Admin Review
    pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
    // Status 4: Confirmed (Manual orders start here)
    confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
    // Status 5: Forwarded to Company
    forwarded: { label: 'Forwarded', color: 'bg-purple-100 text-purple-700', icon: Truck },
    // Status 6: Company Confirmed
    company_confirmed: {
      label: 'Company Confirmed',
      color: 'bg-violet-100 text-violet-700',
      icon: CheckCircle
    },
    // Status 7: Loading (CREDIT CHECK)
    loading: { label: 'Loading', color: 'bg-orange-100 text-orange-700', icon: Truck },
    // Status 8: Loaded
    loaded: { label: 'Loaded', color: 'bg-orange-100 text-orange-700', icon: CheckCircle },
    // Status 9: Invoiced
    invoiced: { label: 'Invoiced', color: 'bg-cyan-100 text-cyan-700', icon: CheckCircle },
    // Status 10: Dispatched
    dispatched: { label: 'Dispatched', color: 'bg-indigo-100 text-indigo-700', icon: Truck },
    // Status 11: Delivered
    delivered: { label: 'Delivered', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
    // Status 12: Completed
    completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    // Status 13: Cancelled
    cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle },
    // Special: On Hold
    on_hold: { label: 'On Hold', color: 'bg-gray-100 text-gray-700', icon: Clock }
  };

  const tabs = [
    { id: 'all', label: 'All Orders' },
    { id: 'pending', label: 'Pending' },
    { id: 'in_progress', label: 'In Progress' },
    { id: 'completed', label: 'Completed' },
    { id: 'cancelled', label: 'Cancelled' }
  ];

  const filteredOrders = orders.filter((order) => {
    const orderNumber = order.order_number || '';
    const distributorName =
      order.distributor_business_name || order.business_name || order.distributor_name || '';
    const orderStatus = order.order_status || order.status || '';

    const matchesSearch =
      orderNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      distributorName.toLowerCase().includes(searchQuery.toLowerCase());

    if (statusFilter === 'all') return matchesSearch;
    if (statusFilter === 'in_progress') {
      return (
        matchesSearch &&
        [
          'forwarded',
          'company_confirmed',
          'loading',
          'loaded',
          'invoiced',
          'dispatched',
          'delivered'
        ].includes(orderStatus)
      );
    }
    return matchesSearch && orderStatus === statusFilter;
  });

  // Pagination
  const totalItems = filteredOrders.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedOrders = filteredOrders.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, paymentStatus, activeDateRange]);

  const handleCancelOrder = async (e, orderId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    setActionLoading(orderId);
    try {
      await updateOrderStatus(orderId, 'cancelled');
      toast.success('Order cancelled successfully');
      fetchOrders();
    } catch (err) {
      toast.error(err.message || 'Failed to cancel order');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivateOrder = async (e, orderId) => {
    e.stopPropagation();
    if (!window.confirm('Reactivate this order?')) return;
    setActionLoading(orderId);
    try {
      await updateOrderStatus(orderId, 'pending');
      toast.success('Order reactivated successfully');
      fetchOrders();
    } catch (err) {
      toast.error(err.message || 'Failed to reactivate order');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRowClick = (orderId) => {
    navigate(`/wholesale/orders/${orderId}`);
  };

  if (loading && orders.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Wholesale Orders</h1>
          <p className="text-gray-500 mt-1">
            Manage and track all wholesale orders
            {localPeriod && (
              <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                {activeDateRange.label}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PeriodFilter
            onChange={handlePeriodChange}
            showLabel={true}
            compact={false}
            storageKey="wholesale_orders"
          />
          <button
            onClick={fetchOrders}
            className="inline-flex items-center gap-2 px-3 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            <Plus className="w-5 h-5" />
            New Order
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {tabs.map((tab) => (
            <Link
              key={tab.id}
              to={tab.id === 'all' ? '/wholesale/orders' : `/wholesale/orders?status=${tab.id}`}
              className={`py-3 border-b-2 text-sm font-medium transition-colors ${
                statusFilter === tab.id
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </Link>
          ))}
        </nav>
      </div>

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search orders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>

        <select
          value={paymentStatus}
          onChange={(e) => setPaymentStatus(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        >
          <option value="all">Every Payment Status</option>
          <option value="outstanding">Outstanding (Unpaid/Partial/Credit)</option>
          <option value="paid">Paid</option>
          <option value="unpaid">Unpaid</option>
          <option value="partial">Partially Paid</option>
          <option value="overdue">Overdue</option>
          <option value="credit">Credit</option>
        </select>
      </div>

      {/* Empty State */}
      {!loading && filteredOrders.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <ShoppingCart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
          <p className="text-gray-500 mb-4">
            {searchQuery || statusFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'Orders will appear here when created'}
          </p>
        </div>
      )}

      {/* Orders Table */}
      {filteredOrders.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">Order</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">
                    Customer
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">Date</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">
                    Status
                  </th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-gray-900">
                    Total
                  </th>
                  <th className="text-center px-6 py-4 text-sm font-semibold text-gray-900">
                    Payment
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedOrders.map((order) => {
                  const orderStatus = order.order_status || order.status || 'pending';
                  const status = statusConfig[orderStatus] || statusConfig.pending;
                  const StatusIcon = status?.icon || Clock;
                  const total = parseFloat(order.total_amount) || 0;
                  const isCancelled = orderStatus === 'cancelled';
                  const isLoading = actionLoading === order.id;

                  return (
                    <tr
                      key={order.id}
                      onClick={() => handleRowClick(order.id)}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <td className="px-6 py-4">
                        <span className="font-medium text-emerald-600">{order.order_number}</span>
                        <p className="text-sm text-gray-500">
                          {order.total_tonnage ? `${order.total_tonnage} tons` : '-'}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-gray-900">
                        {order.distributor_business_name ||
                          order.business_name ||
                          order.distributor_name ||
                          '-'}
                      </td>
                      <td className="px-6 py-4 text-gray-500">
                        {order.order_date ? new Date(order.order_date).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status?.color}`}
                        >
                          <StatusIcon className="w-3 h-3" />
                          {status?.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right font-medium text-gray-900">
                        ₦{total.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {(() => {
                          const paymentStatus = order.payment_status || 'unpaid';
                          const paymentConfig = {
                            paid: { label: 'Paid', color: 'bg-green-100 text-green-700' },
                            credit: { label: 'Credit', color: 'bg-amber-100 text-amber-700' },
                            partial: {
                              label: 'Partially Paid',
                              color: 'bg-blue-100 text-blue-700'
                            },
                            unpaid: { label: 'Unpaid', color: 'bg-gray-100 text-gray-500' },
                            overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700' }
                          };
                          const config = paymentConfig[paymentStatus] || paymentConfig.unpaid;
                          return (
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
                            >
                              {config.label}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} orders
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Order Modal */}
      <CreateOrderModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={fetchOrders}
      />
    </div>
  );
};

export default WholesaleOrdersPage;
